import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { constants, utils } from 'ethers';
import { WalletConnect } from '@web3-react/walletconnect';
import { useOBXContract, useOFTContract } from '@/hooks/useContract';
import { useDidUpdate } from '@/hooks/useDidUpdate';
import { useBalance } from '@/hooks/useBalance';
import { IOFTv2__factory, IOFTv2 } from '@open-blox/interfaces';
import { useWeb3React } from '@web3-react/core';
import { Input, Button, ConnectWallet, message } from '@/components';
import { SelectValueType } from '@/components/Select';
import { useSigner } from '@/hooks/useSigner';
import { validNumber } from '@/utils/misc';
import { ReactComponent as Swap } from '@/assets/icons/exchange.svg';
import { ReactComponent as Obx } from '@/assets/icons/obx.svg';
import { ReactComponent as Fee } from '@/assets/icons/gas-pump.svg';
import { ReactComponent as Time } from '@/assets/icons/clock.svg';
import {
  AVAX_NETWORKS,
  ETH_NETWORKS,
  ARB_NETWORKS,
  CHAINS,
  OFT_ADDRESS,
  ENDPOINT_CHAIN_ID,
  isMainnet
} from '@/constants/chains';
import { CHAIN_IDS } from './chain';
import SelectChain from './SelectChain';
import StaticChain from './StaticChain';
import styles from './home.module.css';

const NUM_REGEX = '^[0-9]*[.,]?[0-9]*$';

const HomeView = () => {
  const [from, setFrom] = useState<SelectValueType>(CHAIN_IDS.AVAX);
  const [to, setTo] = useState<SelectValueType>(CHAIN_IDS.ETH);
  const [amount, setAmount] = useState<string>('');
  const [balance, setBalance] = useState<string>('0.0');
  const [fee, setFee] = useState<string>('0.0001');
  const [visible, setVisible] = useState(false);
  const switchRef = useRef(false);
  const signer = useSigner();
  const updated = useDidUpdate();
  const { chainId, account, connector } = useWeb3React();
  const obx = useOBXContract();
  const oft = useOFTContract();
  const fromChainBalance = useBalance();

  const switchNetwork = useCallback(
    async (chainId: number) => {
      if (isMainnet) {
        if (connector instanceof WalletConnect) {
          await connector.activate(chainId);
        } else {
          await connector.activate(CHAINS[chainId]);
        }
      } else {
        // connect test network
        const CHAIN_ID_MAPPING = {
          [CHAIN_IDS.ETH]: CHAIN_IDS.GOERLI,
          [CHAIN_IDS.ARBITRUM]: CHAIN_IDS.ARB_GOERLI,
          [CHAIN_IDS.AVAX]: CHAIN_IDS.FUJI
        };
        const testChainId = CHAIN_ID_MAPPING[chainId];
        if (connector instanceof WalletConnect) {
          await connector.activate(testChainId);
        } else {
          await connector.activate(CHAINS[testChainId]);
        }
      }
    },
    [connector]
  );

  const swapChain = useCallback(async () => {
    setFrom(to);
    setTo(from);
  }, [from, to]);

  const isAvaxChain = useCallback((chain: SelectValueType) => {
    return chain === CHAIN_IDS.AVAX;
  }, []);

  useEffect(() => {
    if (from && updated) {
      switchRef.current = true;
      switchNetwork(from as number).finally(() => {
        switchRef.current = false;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, switchNetwork]);

  useEffect(() => {
    // default from network
    if (chainId && !switchRef.current) {
      if (AVAX_NETWORKS.includes(chainId)) {
        setFrom(CHAIN_IDS.AVAX);
        setTo(CHAIN_IDS.ETH);
      } else if (ETH_NETWORKS.includes(chainId)) {
        setFrom(CHAIN_IDS.ETH);
        setTo(CHAIN_IDS.AVAX);
      } else if (ARB_NETWORKS.includes(chainId)) {
        setFrom(CHAIN_IDS.ARBITRUM);
        setTo(CHAIN_IDS.AVAX);
      }
    }
  }, [chainId]);

  const swapMax = useCallback(() => {
    setAmount(balance);
  }, [balance]);

  const feeUnit = useMemo(
    () => CHAINS[from as number].nativeCurrency.symbol,
    [from]
  );

  const validAmount = useMemo(
    () => Number(amount) <= Number(balance),
    [amount, balance]
  );

  // const validFee = useMemo(
  //   () => Number(fee) <= Number(utils.formatEther(fromChainBalance)),
  //   [fee, fromChainBalance]
  // );

  const validSend = useMemo(() => validAmount, [validAmount]);

  const buttonText = useMemo(() => {
    // if (!validFee) {
    //   return `Insufficient ${feeUnit} balance`;
    // }
    if (!account) {
      return `Connect Wallet`;
    }
    if (!validAmount) {
      return `Insufficient OBX balance`;
    }
    return 'Send';
  }, [validAmount, account]);

  const changeHandler = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      if (value) {
        if (validNumber(value)) {
          setAmount(value);
        } else {
          setAmount(amount);
        }
      } else {
        setAmount(value);
      }
    },
    [amount]
  );

  const getBalance = useCallback(async () => {
    let balance = '0.0';
    if (account) {
      if (isAvaxChain(from)) {
        if (obx) {
          const obxBalance = await obx.balanceOf(account);
          balance = utils.formatEther(obxBalance);
        }
      } else {
        if (oft) {
          const oftBalance = await oft.balanceOf(account);
          balance = utils.formatEther(oftBalance);
        }
      }
    }
    return balance;
  }, [account, obx, oft, from, isAvaxChain]);

  useEffect(() => {
    getBalance().then(balance => {
      setBalance(balance);
    });
  }, [getBalance]);

  const avaxToETH = useCallback(
    async (amount: string, localAddress: string, remoteChainId: string) => {
      if (signer && account && obx) {
        const deployerAddressBytes32 = utils.defaultAbiCoder.encode(
          ['address'],
          [account]
        );
        // approve
        const etherAmount = utils.parseEther(amount);
        const allowance = await obx.allowance(account, localAddress);
        if (allowance.lt(etherAmount)) {
          await obx.connect(signer).approve(localAddress, etherAmount);
        }
        // calculate native fee from blockchain
        const oft = IOFTv2__factory.connect(localAddress, signer);
        const { nativeFee } = await oft.estimateSendFee(
          remoteChainId,
          deployerAddressBytes32,
          etherAmount,
          false,
          '0x'
        );
        setFee(utils.formatEther(nativeFee));
        if (fromChainBalance.lt(nativeFee)) {
          message.error(`Insufficient ${feeUnit} balance`);
        } else {
          // send
          await oft.sendFrom(
            account,
            remoteChainId,
            deployerAddressBytes32,
            etherAmount,
            [
              account,
              constants.AddressZero,
              '0x'
            ] as unknown as IOFTv2.LzCallParamsStruct,
            { value: nativeFee }
          );
          return true;
        }
      }
    },
    [signer, account, obx, fromChainBalance, feeUnit]
  );

  const ethToAVAX = useCallback(
    async (amount: string, remoteAddress: string, localChainId: string) => {
      if (signer && account) {
        const deployerAddressBytes32 = utils.defaultAbiCoder.encode(
          ['address'],
          [account]
        );
        const etherAmount = utils.parseEther(amount);
        const oft = IOFTv2__factory.connect(remoteAddress, signer);
        const { nativeFee } = await oft.estimateSendFee(
          localChainId,
          deployerAddressBytes32,
          etherAmount,
          false,
          '0x'
        );
        setFee(utils.formatEther(nativeFee));
        if (fromChainBalance.lt(nativeFee)) {
          message.error(`Insufficient ${feeUnit} balance`);
        } else {
          await oft.sendFrom(
            account,
            localChainId,
            deployerAddressBytes32,
            etherAmount,
            [
              account,
              constants.AddressZero,
              '0x'
            ] as unknown as IOFTv2.LzCallParamsStruct,
            { value: nativeFee }
          );
          return true;
        }
      }
    },
    [account, signer, feeUnit, fromChainBalance]
  );

  const sendToken = useCallback(async () => {
    if (!account) {
      setVisible(true);
      return;
    }
    if (!amount) {
      message.error('Please input swap amount!');
    } else {
      if (isAvaxChain(from)) {
        const localAddress = OFT_ADDRESS[from as number];
        const remoteChainId = ENDPOINT_CHAIN_ID[to as number];
        avaxToETH(amount, localAddress, remoteChainId).then(res => {
          if (res) {
            message.success('Swap Success!');
          }
        });
      } else {
        const remoteAddress = OFT_ADDRESS[from as number];
        const localChainId = ENDPOINT_CHAIN_ID[to as number];
        ethToAVAX(amount, remoteAddress, localChainId).then(res => {
          if (res) {
            message.success('Swap Success!');
          }
        });
      }
    }
  }, [account, from, to, amount, avaxToETH, ethToAVAX, isAvaxChain]);

  return (
    <div className={styles.home}>
      <div className={styles.swap}>
        <h1 className={styles.title}>Bridge</h1>
        <div className={styles.section}>
          <span className={styles.sectionText}>From</span>
          {isAvaxChain(from) ? (
            <StaticChain chain={CHAIN_IDS.AVAX} />
          ) : (
            <SelectChain chain={from} setChain={setFrom} />
          )}
        </div>
        <div className={styles.exchangeWrapper}>
          <button type="button" className={styles.exchange} onClick={swapChain}>
            <Swap className={styles.swapIcon} />
          </button>
        </div>
        <div className={styles.section}>
          <span>To</span>
          {isAvaxChain(to) ? (
            <StaticChain chain={CHAIN_IDS.AVAX} />
          ) : (
            <SelectChain chain={to} setChain={setTo} />
          )}
        </div>
        <div className={styles.obx}>
          <span className={styles.obxTitle}>Bridge</span>
          <div className={styles.obxContent}>
            <button type="button" className={styles.obxMax} onClick={swapMax}>
              <span className={styles.obxMaxText}>Max</span>
            </button>
            <Input
              className={styles.obxInput}
              placeholder="0.0"
              type="text"
              inputMode="decimal"
              pattern={NUM_REGEX}
              value={amount}
              onChange={changeHandler}
            />
            <Obx className={styles.obxIcon} />
            <span className={styles.obxUnit}>OBX</span>
          </div>
        </div>
        <div className={styles.balance}>
          <span className={styles.balanceText}>Balance: {balance} OBX</span>
        </div>
        <div className={styles.info}>
          <div className={styles.infoItem}>
            <label className={styles.infoLabel}>
              <Fee className={styles.infoIcon} />
              <span className={styles.infoName}>Gas fee:</span>
            </label>
            <p className={styles.infoValue}>
              {Number(fee).toFixed(4)} {feeUnit}
            </p>
          </div>
          <div className={styles.infoItem}>
            <label className={styles.infoLabel}>
              <Time className={styles.infoIcon} />
              <span className={styles.infoName}>Estimate time:</span>
            </label>
            <p className={styles.infoValue}>15min</p>
          </div>
        </div>
        <div className={styles.send}>
          <Button
            color="primary"
            className={styles.sendButton}
            onClick={sendToken}
            disabled={!validSend}
          >
            {buttonText}
          </Button>
        </div>
        <ConnectWallet visible={visible} setVisible={setVisible} />
      </div>
    </div>
  );
};

export default HomeView;
